import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import Coachmark from './tutorial/Coachmark';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import InventoryBagGrid from './inventory/InventoryBagGrid';
import InventoryItemPopup from './inventory/InventoryItemPopup';
import useInventoryItemPopupActions from './inventory/useInventoryItemPopupActions';
import useInventoryGridDragDrop from './inventory/useInventoryGridDragDrop';
import { resolveSpecialDropBetweenItems } from './inventory/inventoryGridSpecialDrops';
import {
  buildInventoryGridFromBootstrap,
  buildDisplayGridSlotsHidingItem,
  gridItemToEnhanceItem,
  isEnhanceableGridItem,
} from './inventory/inventoryGridBootstrap';
import { ItemImage } from './inventory/inventoryBagGridUtils';
import AuthLayeredCard from './AuthLayeredCard';
import { authButtonStyle } from '../constants/authStyles';
import {
  playArtiBasSound,
  playEnhanceSuccessSound,
  playEnhanceFailSound,
} from '../utils/buttonClickSound';
import { performSocketDrop, isSocketDropBlocked } from '../utils/socketActions';
import { fetchInventoryBootstrap } from '../utils/inventoryBootstrapClient';
import { getActivePlating } from '../utils/ingotPlatingUtils';
import { tl } from '../utils/inlineI18n';

// Kaplamanın başarısız basmada eşyayı yanmaktan koruma şansı (%) — sunucuyla aynı.
const PLATING_ENHANCE_PROTECT = { elmas: 100, altin: 70, gumus: 50 };

const ENHANCE_RESULT_SOUND_DELAY_MS = 450;

const inventoryMenuBgCssVars = (() => {
  const p = process.env.PUBLIC_URL ?? '';
  return {
    '--inv-slot-bg': `url(${p}/image/menu/kutuuzun.png)`,
  };
})();

function getEnhanceFailDisplayMessage(message, t) {
  const failMessage = t('enhanceBroken');
  if (!message || typeof message !== 'string') return failMessage;
  const lower = message.toLowerCase();
  if (
    lower.includes('pq:')
    || lower.includes('foreign key')
    || lower.includes('violates')
    || lower.includes('kırıldı')
    || lower.includes('kirildi')
    || lower.includes('kayboldu')
    || lower.includes('başarısız')
    || lower.includes('basarisiz')
  ) {
    return failMessage;
  }
  return message;
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const EnhancementResultPopup = ({ popup, onClose }) => {
  const { t } = useTranslation('village');
  const { t: tc } = useTranslation('common');
  if (!popup) return null;

  const isLoading = popup.status === 'loading';
  const isSuccess = popup.status === 'success';
  const isFail = popup.status === 'fail';
  const isError = popup.status === 'error';
  const title = isLoading
    ? tc('processing')
    : popup.title || (isSuccess ? tc('success') : isFail ? t('enhanceBurned') : tc('failed'));
  const resultMessage = isSuccess
    ? popup.message
    : isError
      ? popup.message
      : getEnhanceFailDisplayMessage(popup.message, t);

  return (
    <div className="popup-overlay" onClick={isLoading ? undefined : onClose} role="presentation">
      <AuthLayeredCard
        className={`popup-card enhancement-result-popup ${isSuccess ? 'enhancement-result-popup--success' : ''} ${isFail || isError ? 'enhancement-result-popup--fail' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="auth-header popup-header">
          <h3>{title}</h3>
        </div>
        <div className="popup-body">
          {isLoading ? (
            <div className="enhance-popup-loading">
              <div className="enhance-popup-spinner" aria-hidden="true" />
              <p>{t('enhanceEnhancing')}</p>
            </div>
          ) : (
            <p className={isSuccess ? 'enhance-popup-message--success' : 'enhance-popup-message--fail'}>
              {resultMessage}
            </p>
          )}
        </div>
        {!isLoading && (
          <div className="popup-footer">
            <button type="button" className="auth-button village-button--full" style={authButtonStyle} onClick={onClose}>
              {tc('ok')}
            </button>
          </div>
        )}
      </AuthLayeredCard>
    </div>
  );
};

const EnhancementMaterialPopup = ({ material, ownedCount, onClose }) => {
  const { t } = useTranslation('village');
  const { t: tc } = useTranslation('common');
  if (!material) return null;

  return (
    <div className="popup-overlay" onClick={onClose} role="presentation">
      <AuthLayeredCard
        className="popup-card enhancement-material-popup"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="auth-header popup-header">
          <h3>{material.itemName}</h3>
        </div>
        <div className="popup-body enhancement-material-popup-body">
          <div className="enhancement-material-popup-image">
            {material.imageUrl ? (
              <ItemImage imageUrl={material.imageUrl} alt={material.itemName} className="enhancement-material-popup-img" />
            ) : (
              <div className="slot-placeholder-icon">📦</div>
            )}
          </div>
          <p className="enhancement-material-popup-owned">
            {t('enhanceMaterialOwned', { count: ownedCount, required: material.count })}
          </p>
        </div>
        <div className="popup-footer">
          <button type="button" className="auth-button village-button--full" style={authButtonStyle} onClick={onClose}>
            {tc('ok')}
          </button>
        </div>
      </AuthLayeredCard>
    </div>
  );
};

const RequiredItemsDisplay = ({ requiredItems, username, refreshKey = 0, onSatisfiedChange }) => {
  const { t } = useTranslation('village');
  const [itemCounts, setItemCounts] = useState({});
  const [catalogById, setCatalogById] = useState({});
  const [countsLoaded, setCountsLoaded] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState(null);

  useEffect(() => {
    const fetchItemCounts = async () => {
      if (!requiredItems || requiredItems.length === 0 || !username) {
        setItemCounts({});
        setCatalogById({});
        setCountsLoaded(true);
        return;
      }
      const counts = {};
      setCountsLoaded(false);
      try {
        const [invResponse, itemsResponse] = await Promise.all([
          axios.get(`/api/inventory?username=${username}&type=others`),
          axios.get('/api/items?type=others'),
        ]);
        const userItems = invResponse.data?.success ? (invResponse.data.items || []) : [];
        const baseItems = itemsResponse.data?.success ? (itemsResponse.data.items || []) : [];
        const nextCatalogById = {};
        baseItems.forEach((item) => {
          if (item?.id != null) {
            nextCatalogById[item.id] = item;
          }
        });
        setCatalogById(nextCatalogById);
        const countByOtherID = {};
        userItems.forEach((userItem) => {
          if (!userItem?.otherId) return;
          countByOtherID[userItem.otherId] = (countByOtherID[userItem.otherId] || 0) + (userItem.count || 1);
        });
        requiredItems.forEach((reqItem) => {
          if (reqItem.itemType !== 'other') {
            counts[reqItem.itemId] = 0;
            return;
          }
          let targetOtherID = reqItem.itemId;
          if (!targetOtherID && reqItem.itemName) {
            targetOtherID = baseItems.find((item) => item.name === reqItem.itemName)?.id;
          }
          counts[reqItem.itemId] = targetOtherID ? (countByOtherID[targetOtherID] || 0) : 0;
        });
      } catch (err) {
        requiredItems.forEach((reqItem) => { counts[reqItem.itemId] = 0; });
      }
      setItemCounts(counts);
      setCountsLoaded(true);
    };
    fetchItemCounts();
  }, [requiredItems, username, refreshKey]);

  useEffect(() => {
    if (!onSatisfiedChange) return;
    if (!requiredItems?.length) {
      onSatisfiedChange(true);
      return;
    }
    if (!countsLoaded) {
      onSatisfiedChange(false);
      return;
    }
    const allMet = requiredItems.every((reqItem) => {
      const userHasCount = itemCounts[reqItem.itemId] ?? itemCounts[reqItem.itemName] ?? 0;
      return userHasCount >= reqItem.count;
    });
    onSatisfiedChange(allMet);
  }, [requiredItems, itemCounts, countsLoaded, onSatisfiedChange]);

  return (
    <>
      {selectedMaterial && (
        <EnhancementMaterialPopup
          material={selectedMaterial}
          ownedCount={selectedMaterial.ownedCount ?? 0}
          onClose={() => setSelectedMaterial(null)}
        />
      )}
      <div className="required-items-section">
        <div className="required-items-list">
          {requiredItems.map((reqItem, idx) => {
            const userHasCount = itemCounts[reqItem.itemId] ?? itemCounts[reqItem.itemName] ?? 0;
            const hasEnough = userHasCount >= reqItem.count;
            const imageUrl = reqItem.imageUrl || catalogById[reqItem.itemId]?.imageUrl || '';
            const displayName = reqItem.itemName || catalogById[reqItem.itemId]?.name || t('enhanceRequirements');
            return (
              <div key={idx} className={`required-item ${hasEnough ? 'met' : 'not-met'}`}>
                <button
                  type="button"
                  className="required-item-icon-button"
                  onClick={() => setSelectedMaterial({
                    ...reqItem,
                    itemName: displayName,
                    imageUrl,
                    ownedCount: userHasCount,
                  })}
                  aria-label={displayName}
                  title={displayName}
                >
                  {imageUrl ? (
                    <ItemImage imageUrl={imageUrl} alt="" className="required-item-icon" />
                  ) : (
                    <span className="required-item-icon required-item-icon--placeholder" aria-hidden="true">📦</span>
                  )}
                  <span className="required-item-count-badge">{userHasCount}/{reqItem.count}</span>
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

const EnhanceRequirementsPanel = ({
  config,
  selectedItem,
  userGold,
  userCharacter,
  rodFishThresholds,
  pickaxeOreThresholds,
  shovelFarmingThresholds,
  username,
  requirementsRefreshKey,
  onMaterialsSatisfiedChange,
  t,
  tc,
}) => {
  if (!selectedItem) {
    return null;
  }
  if (!config) {
    return (
      <div className="enhance-requirements-panel enhance-requirements-panel--empty">
        <h4>{selectedItem.name} (+{selectedItem.enhancement})</h4>
        <p>{t('enhanceMaxLevel')}</p>
      </div>
    );
  }
  return (
    <div className="enhance-requirements-panel">
      <div className="config-header">
        <h4>{selectedItem.name} (+{config.from} → +{config.to})</h4>
      </div>
      <div className={`config-requirements-row${config.requiredItems?.length > 0 ? ' config-requirements-row--dual' : ''}`}>
        <div className="config-requirements-box config-requirements-box--stats">
          {selectedItem.itemType === 'weapon' && selectedItem.baseItemId === 13 && rodFishThresholds?.length > 0 && (
            <div className={`requirement ${(userCharacter?.fishCaught ?? 0) >= (rodFishThresholds[config.from] ?? 0) ? 'met' : 'not-met'}`}>
              <span>{t('enhanceFishCaughtRequirement', { from: config.from, to: config.to, required: rodFishThresholds[config.from] ?? 0 })}</span>
              <span>{tc('current')}: {userCharacter?.fishCaught ?? 0}</span>
            </div>
          )}
          {selectedItem.itemType === 'weapon' && selectedItem.baseItemId === 20 && pickaxeOreThresholds?.length > 0 && (
            <div className={`requirement ${(userCharacter?.oreMined ?? 0) >= (pickaxeOreThresholds[config.from] ?? 0) ? 'met' : 'not-met'}`}>
              <span>{t('enhanceOreMinedRequirement', { from: config.from, to: config.to, required: pickaxeOreThresholds[config.from] ?? 0 })}</span>
              <span>{tc('current')}: {userCharacter?.oreMined ?? 0}</span>
            </div>
          )}
          {selectedItem.itemType === 'weapon' && selectedItem.baseItemId === 21 && shovelFarmingThresholds?.length > 0 && (
            <div className={`requirement ${(userCharacter?.farmingPoints ?? 0) >= (shovelFarmingThresholds[config.from] ?? 0) ? 'met' : 'not-met'}`}>
              <span>{t('enhanceFarmingPointsRequirement', { from: config.from, to: config.to, required: shovelFarmingThresholds[config.from] ?? 0 })}</span>
              <span>{tc('current')}: {userCharacter?.farmingPoints ?? 0}</span>
            </div>
          )}
          <div className={`requirement ${userGold >= config.goldCost ? 'met' : 'not-met'}`}>
            <span>{tc('yang')}: {config.goldCost}</span>
            <span>{tc('current')}: {userGold}</span>
          </div>
        </div>
        {config.requiredItems?.length > 0 && (
          <div className="config-requirements-box config-requirements-box--materials">
            <RequiredItemsDisplay
              requiredItems={config.requiredItems}
              username={username}
              refreshKey={requirementsRefreshKey}
              onSatisfiedChange={onMaterialsSatisfiedChange}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const buildPreviewGridItem = (gridItem, targetEnhancement, itemCatalog) => {
  if (!gridItem || targetEnhancement == null) return null;
  const catalogList = itemCatalog?.[gridItem.inventoryType] || [];
  const previewBase = catalogList.find(
    (entry) => entry.name === gridItem.baseItem.name
      && Number(entry.enhancement) === Number(targetEnhancement)
  );
  return {
    ...gridItem,
    baseItem: previewBase || { ...gridItem.baseItem, enhancement: targetEnhancement },
  };
};

const EnhanceSlot = ({
  side,
  slotRef,
  selectedItem,
  previewPlus,
  dragOverEnhanceSlot,
  enhanceSlotDragInvalid = false,
  onDragOver,
  onDragLeave,
  onDrop,
  onClear,
  onSlotClick,
  clickable,
  showClear = true,
  acceptDrop = true,
  t,
}) => {
  const isDragOver = acceptDrop && dragOverEnhanceSlot === side;
  const isDragBlocked = isDragOver && enhanceSlotDragInvalid;
  const displayPlus = side === 'right' && previewPlus != null ? previewPlus : selectedItem?.enhancement;
  const showItem = selectedItem && (side !== 'right' || previewPlus != null);
  return (
    <div className={`enhance-slot-column enhance-slot-column--${side}`}>
      <div
        ref={slotRef}
        className={`enhance-slot enhance-slot--${side} inventory-slot-box ${isDragBlocked ? 'enhance-slot--drag-over-invalid' : ''} ${isDragOver && !isDragBlocked ? 'enhance-slot--drag-over' : ''} ${showItem ? 'enhance-slot--filled filled' : 'empty'} ${clickable ? 'enhance-slot--clickable' : ''} ${side === 'right' ? 'enhance-slot--preview' : ''}`}
        onDragOver={acceptDrop ? (event) => onDragOver(event, side) : undefined}
        onDragLeave={acceptDrop ? () => onDragLeave(side) : undefined}
        onDrop={acceptDrop ? (event) => onDrop(event, side) : undefined}
        onClick={() => {
          if (clickable && showItem) onSlotClick?.(side);
        }}
        onKeyDown={(event) => {
          if (!clickable || !showItem) return;
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onSlotClick?.(side);
          }
        }}
        role={clickable && showItem ? 'button' : undefined}
        tabIndex={clickable && showItem ? 0 : undefined}
      >
        {showItem ? (
          <>
            {showClear ? (
              <button
                type="button"
                className="enhance-slot-clear"
                onClick={(event) => {
                  event.stopPropagation();
                  onClear();
                }}
                aria-label={t('enhanceClearSlot')}
              >
                ×
              </button>
            ) : null}
            <div className="enhance-slot-item slot-item-image">
              {selectedItem.imageUrl ? (
                <ItemImage
                  imageUrl={selectedItem.imageUrl}
                  alt={selectedItem.name}
                  singleFrameSprite={selectedItem.itemType === 'boot'}
                />
              ) : (
                <div className="slot-placeholder-icon">📦</div>
              )}
              {displayPlus != null && (
                <span className="enhance-slot-plus">+{displayPlus}</span>
              )}
            </div>
          </>
        ) : (
          <p className="enhance-slot-hint">
            {side === 'right' ? t('enhancePreviewEmpty') : t('enhanceDropHint')}
          </p>
        )}
      </div>
    </div>
  );
};

const BlacksmithWorkbench = ({
  username,
  userCharacter,
  userGold,
  refreshCharacter,
  setError,
  clearError,
}) => {
  const { t } = useTranslation('village');
  const { t: tc } = useTranslation('common');
  const { t: ti } = useTranslation('inventory');

  const [inventoryGridSlots, setInventoryGridSlots] = useState([]);
  const [itemCatalog, setItemCatalog] = useState({});
  const [unlockedSlotsCount, setUnlockedSlotsCount] = useState(60);
  const [currentInventoryPage, setCurrentInventoryPage] = useState(1);
  const [allInventoryItems, setAllInventoryItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedGridItemKey, setSelectedGridItemKey] = useState(null);
  const [enhancementConfigs, setEnhancementConfigs] = useState([]);
  const [enhancing, setEnhancing] = useState(false);
  const [efsunApplyLoading, setEfsunApplyLoading] = useState(false);
  const [platingApplyLoading, setPlatingApplyLoading] = useState(false);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [requirementsRefreshKey, setRequirementsRefreshKey] = useState(0);
  const [dragOverEnhanceSlot, setDragOverEnhanceSlot] = useState(null);
  const [enhanceSlotDragInvalid, setEnhanceSlotDragInvalid] = useState(false);
  const [enhanceResultPopup, setEnhanceResultPopup] = useState(null);
  const [rodFishThresholds, setRodFishThresholds] = useState(null);
  const [pickaxeOreThresholds, setPickaxeOreThresholds] = useState(null);
  const [shovelFarmingThresholds, setShovelFarmingThresholds] = useState(null);
  const [materialsSatisfied, setMaterialsSatisfied] = useState(true);

  const enhanceSlotLeftRef = useRef(null);
  const enhanceSlotRightRef = useRef(null);
  const inventoryGridAreaRef = useRef(null);

  const activeEnhanceConfig = enhancementConfigs[0] ?? null;

  useEffect(() => {
    if (!activeEnhanceConfig?.requiredItems?.length) {
      setMaterialsSatisfied(true);
    } else {
      setMaterialsSatisfied(false);
    }
  }, [activeEnhanceConfig]);
  const displayGridSlots = useMemo(
    () => buildDisplayGridSlotsHidingItem(inventoryGridSlots, selectedGridItemKey),
    [inventoryGridSlots, selectedGridItemKey]
  );

  const inventoryLoadedRef = useRef(false);
  const loadBlacksmithInventory = useCallback(async () => {
    if (!username) return null;
    // "Yükleniyor" sadece ilk açılışta; sonraki yenilemelerde envanter ekranda kalır.
    if (!inventoryLoadedRef.current) setInventoryLoading(true);
    try {
      const response = await fetchInventoryBootstrap(username);
      if (!response.data?.success) throw new Error('bootstrap failed');
      const { gridSlots, allItems, unlockedSlotsCount: nextUnlocked } = await buildInventoryGridFromBootstrap({
        username,
        bootstrapData: response.data,
        equipment: response.data.equipment || null,
        t: ti,
      });
      setInventoryGridSlots(gridSlots);
      setAllInventoryItems(allItems);
      setUnlockedSlotsCount(nextUnlocked);
      setItemCatalog(response.data.catalog || {});
      return { allItems, gridSlots };
    } catch (err) {
      console.error('Error loading blacksmith inventory:', err);
      setError(t('enhanceItemsFetchFailed'));
      return null;
    } finally {
      inventoryLoadedRef.current = true;
      setInventoryLoading(false);
    }
  }, [username, ti, setError, t]);

  useEffect(() => {
    loadBlacksmithInventory();
  }, [loadBlacksmithInventory]);

  useEffect(() => {
    if (selectedItem?.itemType === 'weapon' && selectedItem?.baseItemId === 13) {
      axios.get('/api/fishing/config').then((res) => {
        setRodFishThresholds(Array.isArray(res.data?.config?.rodEnhancementMinFishCaught) ? res.data.config.rodEnhancementMinFishCaught : null);
      }).catch(() => setRodFishThresholds(null));
    } else setRodFishThresholds(null);
  }, [selectedItem]);

  useEffect(() => {
    if (selectedItem?.itemType === 'weapon' && selectedItem?.baseItemId === 20) {
      axios.get('/api/mining/config').then((res) => {
        setPickaxeOreThresholds(Array.isArray(res.data?.config?.pickaxeEnhancementMinOreMined) ? res.data.config.pickaxeEnhancementMinOreMined : null);
      }).catch(() => setPickaxeOreThresholds(null));
    } else setPickaxeOreThresholds(null);
  }, [selectedItem]);

  useEffect(() => {
    if (selectedItem?.itemType === 'weapon' && selectedItem?.baseItemId === 21) {
      axios.get('/api/farming/config').then((res) => {
        setShovelFarmingThresholds(Array.isArray(res.data?.config?.shovelEnhancementMinFarmingPoints) ? res.data.config.shovelEnhancementMinFarmingPoints : null);
      }).catch(() => setShovelFarmingThresholds(null));
    } else setShovelFarmingThresholds(null);
  }, [selectedItem]);

  const {
    activeItemPopup,
    setActiveItemPopup,
    handleGridItemClick: handleInventoryPopupClick,
    buildPopupProps,
  } = useInventoryItemPopupActions({
    username,
    onAfterAction: async (opts) => {
      const savedKey = selectedGridItemKey;
      const result = await loadBlacksmithInventory();
      if (savedKey && !result?.allItems?.some((item) => item.uniqueKey === savedKey)) {
        clearEnhanceSlot();
      }
      if (opts?.refreshGold) {
        await refreshCharacter().catch(() => {});
      }
    },
    setError,
    clearError,
    refreshCharacter,
  });

  const handleItemSelect = async (item) => {
    if (!item) return;
    clearError();
    setSelectedItem(item);
    setSelectedGridItemKey(item.uniqueKey ?? null);
    try {
      const response = await axios.get(
        `/api/enhancement/configs?itemType=${item.itemType}&itemId=${item.baseItemId}&currentEnhancement=${item.enhancement}`
      );
      if (response.data.success) {
        setEnhancementConfigs(response.data.config ? [response.data.config] : []);
      }
    } catch (err) {
      setError(t('enhanceConfigFetchFailed'));
    }
  };

  const clearEnhanceSlot = () => {
    clearError();
    setSelectedItem(null);
    setSelectedGridItemKey(null);
    setEnhancementConfigs([]);
  };

  const assignGridItemToSlot = (slotItem) => {
    if (!slotItem || enhancing || !isEnhanceableGridItem(slotItem)) return;
    if (selectedGridItemKey === slotItem.uniqueKey) return;
    handleItemSelect(gridItemToEnhanceItem(slotItem));
  };

  const getSlottedGridItem = () => (
    selectedGridItemKey
      ? allInventoryItems.find((item) => item.uniqueKey === selectedGridItemKey)
      : null
  );

  const canDropOnEnhanceSlot = (draggedItem) => {
    if (!draggedItem) return false;
    const slottedGridItem = getSlottedGridItem();
    if (slottedGridItem && resolveSpecialDropBetweenItems(draggedItem, slottedGridItem)) {
      return true;
    }
    return isEnhanceableGridItem(draggedItem);
  };

  const refreshAfterSpecialAction = async () => {
    const savedKey = selectedGridItemKey;
    const result = await loadBlacksmithInventory();
    if (savedKey && result?.allItems) {
      const slotItem = result.allItems.find((item) => item.uniqueKey === savedKey);
      if (slotItem) {
        await handleItemSelect(gridItemToEnhanceItem(slotItem));
      }
    }
  };

  const handleApplyPlatingToItem = async (targetKind, userItemId, ingotOtherItemId) => {
    if (!targetKind || !userItemId || !ingotOtherItemId || !username || platingApplyLoading || enhancing) return;
    setPlatingApplyLoading(true);
    clearError();
    try {
      const res = await axios.post('/api/mining/plate', {
        username,
        kind: targetKind,
        userItemId,
        ingotOtherItemId,
      });
      if (res.data.success) {
        setActiveItemPopup(null);
        await refreshAfterSpecialAction();
        if (!res.data.plated) {
          setError(res.data.message || ti('errorPlatingFailed'));
        }
      } else {
        setError(res.data.message || ti('errorPlatingFailed'));
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || ti('errorPlatingFailed'));
    } finally {
      setPlatingApplyLoading(false);
    }
  };

  const handleApplyEfsunToItem = async (targetKind, userItemId, tool) => {
    if (!tool || !username || !targetKind || !userItemId || efsunApplyLoading || enhancing) return;
    let endpoint = '/api/armor-efsun/apply';
    let idPayload = { userArmorId: userItemId };
    if (targetKind === 'weapon') {
      endpoint = '/api/weapon-efsun/apply';
      idPayload = { userWeaponId: userItemId };
    } else if (['helmet', 'boot', 'shield', 'necklace', 'earring'].includes(targetKind)) {
      endpoint = '/api/equipment-efsun/apply';
      idPayload = { kind: targetKind, userItemId };
    }
    setEfsunApplyLoading(true);
    clearError();
    try {
      const res = await axios.post(endpoint, {
        username,
        ...idPayload,
        tool,
      });
      if (res.data.success) {
        setActiveItemPopup(null);
        await refreshAfterSpecialAction();
      } else {
        setError(res.data.message || ti('errorEfsunFailed'));
        if (res.data.code === 'EFSUN_FAILED') {
          setActiveItemPopup(null);
          await refreshAfterSpecialAction();
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || ti('errorEfsunFailed'));
    } finally {
      setEfsunApplyLoading(false);
    }
  };

  const handleSpecialGridDrop = async (drop) => {
    if (drop.type === 'efsun') {
      await handleApplyEfsunToItem(drop.targetKind, drop.userItemId, drop.tool);
      return true;
    }
    if (drop.type === 'plating') {
      await handleApplyPlatingToItem(drop.targetKind, drop.userItemId, drop.ingotOtherItemId);
      return true;
    }
    if (drop.type === 'socketStone' || drop.type === 'socketClear') {
      const ok = await performSocketDrop(drop, { setError });
      if (ok || !isSocketDropBlocked(drop)) await refreshAfterSpecialAction();
      return true;
    }
    return false;
  };

  const tryHandleEnhanceSlotDrop = async (dragged) => {
    if (!dragged) return false;

    const slottedGridItem = getSlottedGridItem();
    const specialDrop = slottedGridItem
      ? resolveSpecialDropBetweenItems(dragged, slottedGridItem)
      : null;

    if (specialDrop) {
      await handleSpecialGridDrop(specialDrop);
      clearEnhanceSlotDragState();
      return true;
    }

    if (!isEnhanceableGridItem(dragged)) {
      setError(t('enhanceItemNotEnhanceable'));
      clearEnhanceSlotDragState();
      return true;
    }

    assignGridItemToSlot(dragged);
    clearEnhanceSlotDragState();
    return true;
  };

  const clearEnhanceSlotDragState = () => {
    setDragOverEnhanceSlot(null);
    setEnhanceSlotDragInvalid(false);
  };

  const isPointOverEnhanceSlot = (x, y) => {
    const leftEl = enhanceSlotLeftRef.current;
    if (!leftEl) return null;
    const rect = leftEl.getBoundingClientRect();
    if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) return 'left';
    return null;
  };

  const updateEnhanceSlotHoverFromPoint = (x, y, draggedItem) => {
    const side = isPointOverEnhanceSlot(x, y);
    if (side === 'left' && draggedItem) {
      setDragOverEnhanceSlot('left');
      setEnhanceSlotDragInvalid(!canDropOnEnhanceSlot(draggedItem));
      return;
    }
    clearEnhanceSlotDragState();
  };

  const {
    draggedSlotIndex,
    dragOverInventoryIndex,
    touchDropTargetIndex,
    isTouchDragging,
    touchDragGhost,
    skipNextClickRef,
    getDraggedGridItem,
    isGridDropBlocked,
    handleGridDragStart,
    handleGridDragEnd,
    handleInventorySlotDragOver,
    handleInventorySlotDragLeave,
    handleGridDrop,
    handleInventoryTabDragOver,
    handleGridTouchStart,
    handleGridTouchMove,
    handleGridTouchEnd,
    handleGridTouchCancel,
  } = useInventoryGridDragDrop({
    username,
    inventoryGridSlots,
    setInventoryGridSlots,
    unlockedSlotsCount,
    currentInventoryPage,
    setCurrentInventoryPage,
    inventoryGridAreaRef,
    setError,
    errorDropBlockedMessage: ti('errorDropBlocked'),
    layoutSaveFailedMessage: ti('errorLayoutSaveFailed'),
    disabled: enhancing || efsunApplyLoading || platingApplyLoading,
    onSpecialGridDrop: handleSpecialGridDrop,
    onInventoryChanged: refreshAfterSpecialAction,
    potStackFullMessage: ti('errorPotStackFull', { max: 200 }),
    onBeforeGridTouchDrop: async ({ x, y, draggedItem }) => {
      if (!isPointOverEnhanceSlot(x, y)) return false;
      return tryHandleEnhanceSlotDrop(draggedItem);
    },
  });

  const handleEnhanceSlotDragOver = (event, side) => {
    if (side !== 'left') return;
    event.preventDefault();
    const dragged = getDraggedGridItem(event);
    if (!dragged) return;
    setDragOverEnhanceSlot('left');
    const canDrop = canDropOnEnhanceSlot(dragged);
    setEnhanceSlotDragInvalid(!canDrop);
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = canDrop ? 'move' : 'none';
    }
  };

  const handleEnhanceSlotDragLeave = () => {
    clearEnhanceSlotDragState();
  };

  const handleEnhanceSlotDrop = async (event, side) => {
    if (side !== 'left') return;
    event.preventDefault();
    const dragged = getDraggedGridItem(event);
    if (dragged) {
      await tryHandleEnhanceSlotDrop(dragged);
    } else {
      clearEnhanceSlotDragState();
    }
  };

  const handleGridDragEndWithEnhance = () => {
    clearEnhanceSlotDragState();
    handleGridDragEnd();
  };

  const handleGridTouchMoveWithEnhance = (event) => {
    handleGridTouchMove(event);
    const touch = event.touches?.[0];
    if (!touch || draggedSlotIndex === null) return;
    updateEnhanceSlotHoverFromPoint(
      touch.clientX,
      touch.clientY,
      inventoryGridSlots[draggedSlotIndex]
    );
  };

  const handleGridTouchEndWithEnhance = (event) => {
    handleGridTouchEnd(event);
    clearEnhanceSlotDragState();
  };

  const handleGridItemClick = (event, slotItem, index) => {
    if (skipNextClickRef.current || !slotItem) {
      skipNextClickRef.current = false;
      return;
    }
    handleInventoryPopupClick(slotItem, index, inventoryGridAreaRef);
  };

  const handleEnhanceSlotClick = (side) => {
    if (!selectedItem) return;
    const gridItem = allInventoryItems.find((item) => item.uniqueKey === selectedGridItemKey);
    if (!gridItem) return;
    if (side === 'left') {
      setActiveItemPopup({ index: -1, item: gridItem, slotMode: 'left' });
      return;
    }
    if (side === 'right' && activeEnhanceConfig) {
      const previewItem = buildPreviewGridItem(gridItem, activeEnhanceConfig.to, itemCatalog);
      if (previewItem) {
        setActiveItemPopup({ index: -2, item: previewItem, slotMode: 'preview' });
      }
    }
  };

  const checkEnhancementRequirements = (config) => {
    if (!userGold || !config) return false;
    if (!materialsSatisfied) return false;
    if (userGold < config.goldCost) return false;
    if (selectedItem?.itemType === 'weapon' && selectedItem?.baseItemId === 13 && rodFishThresholds?.length) {
      const need = rodFishThresholds[config.from] ?? 0;
      if (need > 0 && (userCharacter?.fishCaught ?? 0) < need) return false;
    }
    if (selectedItem?.itemType === 'weapon' && selectedItem?.baseItemId === 20 && pickaxeOreThresholds?.length) {
      const need = pickaxeOreThresholds[config.from] ?? 0;
      if (need > 0 && (userCharacter?.oreMined ?? 0) < need) return false;
    }
    if (selectedItem?.itemType === 'weapon' && selectedItem?.baseItemId === 21 && shovelFarmingThresholds?.length) {
      const need = shovelFarmingThresholds[config.from] ?? 0;
      if (need > 0 && (userCharacter?.farmingPoints ?? 0) < need) return false;
    }
    return true;
  };

  const reselectAfterRefresh = async (savedKey) => {
    const result = await loadBlacksmithInventory();
    if (!savedKey || !result?.allItems) {
      clearEnhanceSlot();
      return;
    }
    const slotItem = result.allItems.find((item) => item.uniqueKey === savedKey);
    if (slotItem) {
      await handleItemSelect(gridItemToEnhanceItem(slotItem));
    } else {
      clearEnhanceSlot();
    }
  };

  const handleEnhance = async (config) => {
    if (!selectedItem || !config) return;
    if (!checkEnhancementRequirements(config)) {
      setError(t('enhanceRequirementsNotMet'));
      return;
    }
    const savedKey = selectedGridItemKey;
    playArtiBasSound();
    setEnhancing(true);
    clearError();
    setEnhanceResultPopup({ status: 'loading' });
    const startedAt = Date.now();
    const waitForReveal = async () => {
      const remaining = ENHANCE_RESULT_SOUND_DELAY_MS - (Date.now() - startedAt);
      if (remaining > 0) await sleep(remaining);
    };

    try {
      const response = await axios.post('/api/enhancement/enhance', {
        username,
        itemType: selectedItem.itemType,
        itemId: selectedItem.id,
      });

      if (response.data.success) {
        await waitForReveal();
        playEnhanceSuccessSound();
        setEnhanceResultPopup({ status: 'success', title: tc('success'), message: t('enhanceSuccess') });
        await refreshCharacter().catch(() => {});
        await reselectAfterRefresh(savedKey);
        setRequirementsRefreshKey((prev) => prev + 1);
      } else if (response.data.protected) {
        // Kaplama korudu: eşya yanmadı, seçili kalır.
        await refreshCharacter().catch(() => {});
        await reselectAfterRefresh(savedKey);
        setRequirementsRefreshKey((prev) => prev + 1);
        await waitForReveal();
        setEnhanceResultPopup({
          status: 'error',
          title: tl('Kaplama korudu', 'Plating protected'),
          message: response.data.message,
        });
      } else {
        const itemDestroyed = response.data.itemDestroyed === true;
        clearEnhanceSlot();
        await loadBlacksmithInventory();
        await refreshCharacter().catch(() => {});
        await waitForReveal();
        if (itemDestroyed) playEnhanceFailSound();
        setEnhanceResultPopup({
          status: itemDestroyed ? 'fail' : 'error',
          title: itemDestroyed ? t('enhanceBurned') : t('enhanceFailed'),
          message: itemDestroyed ? t('enhanceBroken') : getEnhanceFailDisplayMessage(response.data.message, t),
        });
      }
    } catch (err) {
      clearEnhanceSlot();
      await loadBlacksmithInventory();
      await waitForReveal();
      playEnhanceFailSound();
      setEnhanceResultPopup({
        status: 'fail',
        title: t('enhanceBurned'),
        message: getEnhanceFailDisplayMessage(err.response?.data?.message, t),
      });
    } finally {
      setEnhancing(false);
    }
  };

  const rightPreviewPlus = selectedItem && activeEnhanceConfig ? activeEnhanceConfig.to : null;

  const basePopupProps = buildPopupProps();
  const itemPopupProps = basePopupProps ? {
    ...basePopupProps,
    ...(activeItemPopup?.slotMode === 'preview' && {
      hideActions: true,
      previewNote: t('enhancePreviewNote'),
    }),
    ...(activeItemPopup?.slotMode === 'left' && {
      hideActions: true,
      onRemoveFromEnhanceSlot: () => {
        basePopupProps.onClose();
        clearEnhanceSlot();
      },
      enhanceRemoveFromSlotLabel: t('enhanceClearSlot'),
    }),
    ...(!activeItemPopup?.slotMode
      && activeItemPopup?.item
      && isEnhanceableGridItem(activeItemPopup.item)
      && activeItemPopup.item.uniqueKey !== selectedGridItemKey && {
      onPutInEnhanceSlot: () => {
        assignGridItemToSlot(activeItemPopup.item);
        basePopupProps.onClose();
      },
      enhancePutInSlotLabel: t('enhancePutInSlot'),
    }),
  } : null;

  return (
    <>
      {enhanceResultPopup && (
        <EnhancementResultPopup popup={enhanceResultPopup} onClose={() => setEnhanceResultPopup(null)} />
      )}
      {itemPopupProps ? <InventoryItemPopup {...itemPopupProps} /> : null}
      <div className="enhancement-section" style={inventoryMenuBgCssVars}>
        <Coachmark
          tourId="blacksmith"
          steps={[
            { selector: '.enhance-workbench', textKey: 'blacksmithWorkbench' },
            { selector: '', textKey: 'blacksmithEfsun' },
        ]}
        />
        <div className="enhance-workbench">
          <div className="enhance-workbench-flow">
            <div className="enhance-workbench-slots">
              <EnhanceSlot
                side="left"
                slotRef={enhanceSlotLeftRef}
                selectedItem={selectedItem}
                previewPlus={selectedItem?.enhancement}
                dragOverEnhanceSlot={dragOverEnhanceSlot}
                enhanceSlotDragInvalid={enhanceSlotDragInvalid}
                onDragOver={handleEnhanceSlotDragOver}
                onDragLeave={handleEnhanceSlotDragLeave}
                onDrop={handleEnhanceSlotDrop}
                onClear={clearEnhanceSlot}
                onSlotClick={handleEnhanceSlotClick}
                clickable
                t={t}
              />
              <div className="enhance-slot-arrow" aria-hidden="true">→</div>
              <EnhanceSlot
                side="right"
                slotRef={enhanceSlotRightRef}
                selectedItem={selectedItem}
                previewPlus={rightPreviewPlus}
                dragOverEnhanceSlot={dragOverEnhanceSlot}
                onDragOver={handleEnhanceSlotDragOver}
                onDragLeave={() => setDragOverEnhanceSlot(null)}
                onDrop={handleEnhanceSlotDrop}
                onClear={clearEnhanceSlot}
                onSlotClick={handleEnhanceSlotClick}
                clickable={!!activeEnhanceConfig}
                showClear={false}
                acceptDrop={false}
                t={t}
              />
            </div>

            <EnhanceRequirementsPanel
              config={activeEnhanceConfig}
              selectedItem={selectedItem}
              userGold={userGold}
              userCharacter={userCharacter}
              rodFishThresholds={rodFishThresholds}
              pickaxeOreThresholds={pickaxeOreThresholds}
              shovelFarmingThresholds={shovelFarmingThresholds}
              username={username}
              requirementsRefreshKey={requirementsRefreshKey}
              onMaterialsSatisfiedChange={setMaterialsSatisfied}
              t={t}
              tc={tc}
            />

            <div className="enhance-actions">
              {selectedItem && activeEnhanceConfig && (
                <span className="success-rate enhance-actions-success-rate">
                  {t('enhanceSuccessRate', { rate: activeEnhanceConfig.successRate })}
                </span>
              )}
              {selectedItem && activeEnhanceConfig && (() => {
                const plating = getActivePlating(selectedItem);
                const chance = plating ? PLATING_ENHANCE_PROTECT[plating.oreId] : 0;
                if (!chance) return null;
                return (
                  <span className="success-rate enhance-plating-protect" style={{ color: plating.color || '#9be29b' }}>
                    🛡️ {tl(`${plating.label}: başarısız olursa %${chance} ihtimalle eşya yanmaz`, `${plating.label}: ${chance}% chance the item won't burn on failure`)}
                  </span>
                );
              })()}
              <button
                type="button"
                className={`auth-button village-button--full village-enhance-button ${activeEnhanceConfig && checkEnhancementRequirements(activeEnhanceConfig) ? 'enabled' : 'disabled'}`}
                style={authButtonStyle}
                data-silent-click="true"
                onClick={() => activeEnhanceConfig && handleEnhance(activeEnhanceConfig)}
                disabled={!selectedItem || !activeEnhanceConfig || !checkEnhancementRequirements(activeEnhanceConfig) || enhancing}
              >
                {enhancing ? tc('processing') : t('enhanceButton')}
              </button>
            </div>
          </div>

          <div className="inventory-list enhance-inventory-section">
            {inventoryLoading ? (
              <p className="no-items">{t('enhanceLoadingInventory')}</p>
            ) : (
              <div
                className="enhance-inventory-touch-host"
                onTouchMoveCapture={handleGridTouchMoveWithEnhance}
                onTouchEndCapture={handleGridTouchEndWithEnhance}
                onTouchCancelCapture={handleGridTouchCancel}
              >
                <InventoryBagGrid
                  gridSlots={displayGridSlots}
                  currentInventoryPage={currentInventoryPage}
                  onPageChange={setCurrentInventoryPage}
                  unlockedSlotsCount={unlockedSlotsCount}
                  dragOverIndex={touchDropTargetIndex ?? dragOverInventoryIndex}
                  isDropBlocked={isGridDropBlocked}
                  draggable={(slotItem) => !!slotItem && !enhancing && !efsunApplyLoading && !platingApplyLoading}
                  onDragStart={handleGridDragStart}
                  onDragOver={handleInventorySlotDragOver}
                  onDragLeave={handleInventorySlotDragLeave}
                  onDrop={handleGridDrop}
                  onDragEnd={handleGridDragEndWithEnhance}
                  onTouchStart={handleGridTouchStart}
                  onClick={handleGridItemClick}
                  onPageTabDragOver={handleInventoryTabDragOver}
                  slotKeyPrefix="blacksmith-inventory"
                  wrapperClassName="enhance-inventory-grid-wrapper"
                  areaRef={inventoryGridAreaRef}
                  emptySlotTitle={ti('slotEmpty')}
                  lockedSlotTitle={ti('slotLocked')}
                  isTouchDragging={isTouchDragging}
                />
              </div>
            )}
          </div>
        </div>

        {touchDragGhost && (
          <div className="touch-drag-ghost" style={{ left: `${touchDragGhost.x}px`, top: `${touchDragGhost.y}px` }}>
            {touchDragGhost.imageUrl ? (
              <img src={touchDragGhost.imageUrl} alt="" />
            ) : (
              <div className="slot-placeholder-icon">📦</div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default BlacksmithWorkbench;
